/* eslint-disable jsx-a11y/mouse-events-have-key-events */

// TODO: add back in if uncommenting stuff below
// import { useStoreState } from 'easy-peasy';
import { PropTypes } from 'prop-types';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { withTheme } from 'styled-components';

import { Badge } from '@ge/components/badge';
import { Icon, Icons } from '@ge/components/icon';
import { globalColors } from '@ge/tokens/colors';
import { roundNumber } from '@ge/util';
import { MobileBreakpoints as Breakpoints } from '@ge/web-client/src/assets/styles/mixins';

const StyledCountCell = styled(Badge)`
  opacity: ${(props) => (!props.count ? 0.15 : 1)};
  ${Breakpoints('mobileXL')`
    min-width: 30px;
    padding: 0 4px;
  `}
  ${Breakpoints('mobileM')`
    min-width: 26px;
    padding: 0 4px;
  `}
  ${Breakpoints('mobileS')`
    min-width: 24px;
    padding: 0 4px;
  `}
`;

const TurbineIcon = styled(Icon).attrs((props) => ({
  size: props.size,
  icon: Icons.TURBINE,
  color: props.theme.entityDetails.siteHeaderIconColor,
}))``;
const SiteIcon = styled(Icon).attrs((props) => ({
  size: 24,
  icon: Icons.SITE,
  color: props.theme.entityDetails.siteHeaderIconColor,
}))``;

const OfflineIcon = styled(Icon).attrs((props) => ({
  size: 12,
  icon: Icons.OFFLINE,
  color: props.theme.entityDetails.siteHeaderIconColor,
}))``;

const SiteDetails = styled.div`
  color: ${globalColors.grey4};
  font-size: 11px;
  font-weight: 500;
  font-family: 'Museo Sans';
  font-style: italic;
  margin: 9px 0 6px 18px;
  .turbine-subdetails {
    color: ${globalColors.grey2};
  }
  .turbine-separator {
    color: #979797;
    margin: 5px;
  }
`;
const ChevronIconRight = styled(Icon).attrs((props) => ({
  size: 12,
  icon: Icons.CHEVRON,
  color: props.theme.entityDetails.siteHeaderIconColor,
  rotate: -90,
}))`
  margin-left: auto !important;
  padding-right: 19px;
  padding-left: 17px;
`;
const AvailText = styled.span`
   {
    font-style: italic;
    color: ${globalColors.grey12};
  }
`;

export const RenderSiteList = ({ production, conditions, assets, name, theme }) => {
  const { t } = useTranslation(['general'], {
    useSuspense: false,
  });

  /**
   * Checking the undefined value.
   */
  const getValue = (params, obj, uom) => {
    let keyValue = params[0];
    let keyObj = obj;

    for (let i = 0; i < params.length; i++) {
      keyValue = params[i];

      if (keyObj && typeof keyObj[keyValue] !== 'undefined') {
        keyObj = keyObj[keyValue];
      } else {
        return '-';
      }
    }

    return uom ? `${roundNumber(keyObj, 1)} ${uom}` : roundNumber(keyObj, 1);
  };

  const entityDialogDescription = useCallback(() => {
    const { availablePercent, offline, turbineCount } = assets;
    return (
      <>
        <h3 className="sitesRow">
          <TurbineIcon size={12} /> {turbineCount}
        </h3>
        <h3 className="sitesRow">
          <OfflineIcon /> {offline || '-'}
        </h3>
        <h3 className="sitesRow">
          <AvailText>Avail.</AvailText>{' '}
          {availablePercent ? `${(availablePercent * 100).toFixed(1)}%` : '-'}
        </h3>
      </>
    );
  }, [assets]);

  const productionDetails = useCallback(
    () => (
      <>
        {t('mobile.power', 'Power')}:{' '}
        <span className="turbine-subdetails">{getValue(['actualPower'], production, 'MW')}</span>
        <span className="turbine-separator">|</span>
      </>
    ),
    [production, t],
  );
  const conditionDetails = useCallback(
    () => (
      <>
        {t('mobile.wind', 'Wind')}:{' '}
        <span className="turbine-subdetails">{getValue(['windMS'], conditions, 'm/s')}</span>
        <span className="turbine-separator">|</span>
        {t('mobile.temp', 'Temp')}:{' '}
        <span className="turbine-subdetails">{getValue(['tempC'], conditions, '°C')}</span>
      </>
    ),
    [conditions, t],
  );
  const {
    available,
    impacted,
    maintenance,
    netComm,
    noData,
    online,
    repair,
    stopped,
    tripped,
  } = assets;
  return (
    <>
      <div className="sitesList">
        <SiteIcon className="titleRow" />
        <h2>{name}</h2>
        {entityDialogDescription()}
      </div>
      <div className="whole-badge">
        <div className="whole-badge-wrapper">
          <StyledCountCell
            count={online}
            className="site-badge-first"
            middle
            label={online || '-'}
            color={`${theme.siteStatusBar.online}`}
          />
          <StyledCountCell
            count={available}
            className="site-badge"
            middle
            label={available || '-'}
            color={`${theme.siteStatusBar.available}`}
          />
          <StyledCountCell
            count={impacted}
            className="site-badge"
            middle
            label={impacted || '-'}
            color={`${theme.siteStatusBar.impacted}`}
          />
          <StyledCountCell
            count={tripped}
            className="site-badge"
            middle
            label={tripped || '-'}
            color={`${theme.siteStatusBar.tripped}`}
          />
          <StyledCountCell
            count={stopped}
            className="site-badge"
            middle
            label={stopped || '-'}
            color={`${theme.siteStatusBar.stopped}`}
          />
          <StyledCountCell
            count={noData || netComm}
            className="site-badge"
            middle
            label={noData || netComm || '-'}
            color={`${theme.siteStatusBar.noData}`}
          />
          <StyledCountCell
            count={maintenance}
            className="site-badge"
            middle
            label={maintenance || '-'}
            color={`${theme.siteStatusBar.maintenance}`}
          />
          <StyledCountCell
            count={repair}
            className="site-badge-last"
            middle
            label={repair || '-'}
            color={`${theme.siteStatusBar.repair}`}
          />
        </div>
        <ChevronIconRight className="arrow-right" />
      </div>
      <SiteDetails>
        {productionDetails()}
        {conditionDetails()}
      </SiteDetails>
    </>
  );
};

RenderSiteList.propTypes = {
  conditions: PropTypes.instanceOf(Object),
  assets: PropTypes.instanceOf(Object),
  production: PropTypes.instanceOf(Object),
  name: PropTypes.string,
  id: PropTypes.string,
  theme: PropTypes.instanceOf(Object),
  onChangeHandler: PropTypes.func,
};

RenderSiteList.defaultProps = {
  conditions: {},
  assets: {},
  production: {},
  name: '',
  key: '',
};

export default React.memo(withTheme(RenderSiteList));
